import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, Loader2, MessageCircle, MessageSquareReply, Target, Mail, ChevronRight } from 'lucide-react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import EnableNotificationsButton from '../../components/EnableNotificationsButton';

const TYPE_META = {
  post: { icon: MessageCircle, verb: 'posted in Community' },
  comment: { icon: MessageSquareReply, verb: 'commented in Community' },
  intention: { icon: Target, verb: 'set a weekly intention' },
  coach_message: { icon: Mail, verb: 'messaged a coach' },
};

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

const AlertRow = ({ alert, onOpen }) => {
  const meta = TYPE_META[alert.type] || TYPE_META.post;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(alert)}
      className={`w-full text-left flex items-start gap-3 px-4 py-4 border-b border-stone-200 last:border-b-0 hover:bg-stone-50 transition-colors ${alert.is_read ? '' : 'bg-[#EFB342]/10'}`}
      data-testid={`admin-alert-${alert.id}`}
    >
      <span className="w-9 h-9 rounded-lg bg-[#EFB342]/15 text-[#B8871E] flex items-center justify-center shrink-0"><Icon size={17} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-stone-900">
          <strong>{alert.user_name}</strong> {meta.verb}
          {!alert.is_read && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500 align-middle" data-testid={`admin-alert-unread-${alert.id}`} />}
        </span>
        {alert.snippet && <span className="block text-sm text-stone-600 mt-0.5 line-clamp-2">&ldquo;{alert.snippet}&rdquo;</span>}
        <span className="block text-xs text-stone-400 mt-1">{timeAgo(alert.created_at)}</span>
      </span>
      {alert.type !== 'coach_message' && <ChevronRight size={16} className="text-stone-400 mt-2 shrink-0" />}
    </button>
  );
};

export default function AdminInbox() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    apiClient.get('/admin/alerts')
      .then((res) => {
        setAlerts(res.data.alerts);
        if (res.data.unread_count > 0) {
          apiClient.post('/admin/alerts/mark-all-read')
            .then(() => window.dispatchEvent(new Event('adminAlertsRead')))
            .catch(() => {});
        }
      })
      .catch(() => setAlerts([]));
  }, []);

  const openAlert = (alert) => {
    if (alert.link && alert.link !== '/admin/inbox') navigate(alert.link);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F4F1] text-stone-900 p-4 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <header className="mb-5">
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <Inbox className="text-[#EFB342]" size={26} /> Inbox
            </h1>
            <p className="text-stone-600 mt-1 text-sm">
              New community posts, comments, weekly intentions and coach messages from members. Pop-up alerts are bundled every 15 minutes.
            </p>
          </header>

          <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-5" data-testid="admin-inbox-push-setup">
            <p className="text-sm text-stone-700 mb-3">Turn on pop-up alerts on each device you use. On a computer, click the button below and choose <strong>Allow</strong>. On your phone, open RxGRIT from its home-screen icon and come to this page.</p>
            <EnableNotificationsButton allowBrowser />
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="admin-inbox-list">
            {alerts === null ? (
              <div className="flex items-center justify-center py-16 text-stone-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
            ) : alerts.length === 0 ? (
              <p className="text-center py-16 text-stone-500" data-testid="admin-inbox-empty">Nothing new yet. Member activity will show up here.</p>
            ) : (
              alerts.map((a) => <AlertRow key={a.id} alert={a} onOpen={openAlert} />)
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
